/**
 * ResourceManager.js - Resource lifecycle management with essential error tracking
 * ----------------------------------------------------------------------------
 * Manages the lifecycle and cleanup of core calendar resources.
 *
 * Tracks and controls access to two key objects:
 * - `calendarRenderer`: the UI renderer (e.g., FullCalendar adapter)
 * - `calendarInstance`: the live calendar instance (e.g., FullCalendar object)
 *
 * Responsibilities:
 * - Set and retrieve the renderer and instance with error tracking
 * - Check availability with `hasCalendarRenderer()` and `hasCalendarInstance()`
 * - Cleanly destroy both resources via `cleanup()` with error handling
 * - Essential error tracking for critical resource management failures
 *
 * Features:
 * - Defensive null checks and type-safe `destroy()` calls
 * - Async cleanup support for both renderer and instance
 * - Clears internal references to prevent memory leaks
 * - Essential error tracking for resource lifecycle failures
 *
 * Key Methods:
 * - setCalendarRenderer()/setCalendarInstance() - Resource setting with validation
 * - getCalendarRenderer()/getCalendarInstance() - Resource retrieval
 * - cleanup() - Resource destruction with error handling
 * - getRecentErrors() - Recent failure debugging
 * - hasCriticalIssues() - Quick error status check
 * - clearErrorHistory() - Reset error tracking
 *
 * Error Categories:
 * - initialization: Resource manager creation failures
 * - configuration: Resource setting and availability failures
 * - cleanup: Resource destruction failures (formerly 'initialization')
 * - lifecycle: Resource state management failures
 *
 * Limitations:
 * - Does not automatically manage initialization or re-creation
 * - Not event-driven (no observer pattern or notifications)
 * - Assumes `destroy()` is a synchronous or awaitable method on both resources
 *
 * Note: Ultra-minimal error tracking approach - focuses only on critical failures
 * that could break resource management functionality.
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

// Essential error tracking setup (ultra-minimal)
const errorHandler = new ErrorHandler({
  module: 'ResourceManager',
  maxErrors: 5
});

export default class ResourceManager {
  constructor() {
    this.calendarRenderer = null;
    this.calendarInstance = null;

    errorHandler.logSuccess('ResourceManager initialized successfully', {
      category: 'initialization',
      action: 'manager-init-success'
    });
  }

  setCalendarRenderer(renderer) {
    this.calendarRenderer = renderer;

    if (!renderer) {
      errorHandler.handleError('Null renderer set in ResourceManager', null, {
        category: 'configuration',
        action: 'null-renderer-set'
      });
    }
  }

  setCalendarInstance(instance) {
    this.calendarInstance = instance;

    if (!instance) {
      errorHandler.handleError('Null instance set in ResourceManager', null, {
        category: 'configuration',
        action: 'null-instance-set'
      });
    }
  }

  getCalendarRenderer() {
    return this.calendarRenderer;
  }

  getCalendarInstance() {
    return this.calendarInstance;
  }

  hasCalendarInstance() {
    return !!this.calendarInstance;
  }

  hasCalendarRenderer() {
    return !!this.calendarRenderer;
  }

  /**
   * Clean up all managed resources
   * Both renderer and instance destroy() methods are awaited if they return Promises
   * @returns {Promise<void>}
   */
  async cleanup() {
    let instanceCleanupSuccess = true;
    let rendererCleanupSuccess = true;

    // Cleanup calendar instance
    if (this.calendarInstance) {
      instanceCleanupSuccess = await this._destroyResource(
        this.calendarInstance,
        'calendar instance',
        'instance'
      );
    }

    // Cleanup calendar renderer
    if (this.calendarRenderer) {
      rendererCleanupSuccess = await this._destroyResource(
        this.calendarRenderer,
        'calendar renderer',
        'renderer'
      );
    }

    // Clear references regardless of cleanup success
    this.calendarInstance = null;
    this.calendarRenderer = null;

    // Log final cleanup status
    if (instanceCleanupSuccess && rendererCleanupSuccess) {
      errorHandler.logSuccess('Resource cleanup completed successfully', {
        category: 'cleanup',
        action: 'cleanup-success'
      });
    } else {
      errorHandler.handleError('Resource cleanup completed with some failures', null, {
        category: 'cleanup',
        action: 'cleanup-partial-failure',
        instanceSuccess: instanceCleanupSuccess,
        rendererSuccess: rendererCleanupSuccess
      });
    }
  }

  /**
   * Safely destroy a single resource
   * Handles both sync and async destroy methods
   *
   * @param {Object} resource - The resource to destroy
   * @param {string} resourceName - Human-readable name for error messages
   * @param {string} resourceType - Short identifier (instance/renderer)
   * @returns {Promise<boolean>} True if cleanup succeeded
   * @private
   */
  async _destroyResource(resource, resourceName, resourceType) {
    try {
      if (typeof resource.destroy !== 'function') {
        errorHandler.handleError(`${resourceName} missing destroy method`, null, {
          category: 'configuration',
          action: `${resourceType}-no-destroy-method`
        });
        // Not having a destroy method is a warning, not a failure
        return true;
      }

      // Call destroy and await if it returns a Promise
      const result = resource.destroy();
      if (result && typeof result.then === 'function') {
        await result;
      }

      return true;
    } catch (error) {
      // FIXED: Use 'cleanup' category instead of 'initialization'
      // Destruction errors are cleanup/lifecycle issues, not initialization issues
      errorHandler.handleError(`Error destroying ${resourceName}`, error, {
        category: 'cleanup',
        action: `${resourceType}-destroy-failed`
      });
      return false;
    }
  }

  /**
   * Get recent errors for debugging
   * @param {number} minutes - Minutes to look back (default: 5)
   * @returns {Array} Recent error entries
   */
  getRecentErrors(minutes = 5) {
    return errorHandler.getRecentErrors(minutes);
  }

  /**
   * Check if there are critical issues that need attention
   * @returns {boolean} True if critical issues exist
   */
  hasCriticalIssues() {
    return errorHandler.hasCriticalIssues();
  }

  /**
   * Clear error history (useful for testing or after resolving issues)
   */
  clearErrorHistory() {
    errorHandler.clearErrorHistory();
  }

  /**
   * Get comprehensive stats including error information
   * @returns {Object} Stats object with error data
   */
  getStats() {
    const errorStats = errorHandler.getErrorStats();

    return {
      hasRenderer: this.hasCalendarRenderer(),
      hasInstance: this.hasCalendarInstance(),
      rendererType: this.calendarRenderer ? this.calendarRenderer.constructor.name : null,
      instanceType: this.calendarInstance ? this.calendarInstance.constructor.name : null,
      errors: {
        total: errorStats.totalErrors,
        fatal: errorStats.fatalErrors,
        recent: errorStats.recentErrors,
        byCategory: errorStats.errorsByCategory
      }
    };
  }
}
